// useCachedImage: loads an image by Supabase storagePath with a
// cache-then-fallback strategy.
//
// Behavior matrix:
//   OFFLINE_ENABLED=false  -> exactly the old behavior: createSignedUrl
//                            and use that URL directly. No caching.
//   OFFLINE_ENABLED=true   -> still tries createSignedUrl first (so
//                            online users see fresh images). On success
//                            we fetch the blob in the background and
//                            stash it in the Cache API by storagePath.
//                            If the signed URL call fails (offline or
//                            token/session expired), we look for the
//                            blob in the Cache API and serve it via
//                            URL.createObjectURL.
//
// Returns a ready-to-use src string or null while resolving. Object
// URLs are revoked in cleanup to avoid leaks.

import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { OFFLINE_ENABLED, CACHE_IMAGES } from '../../config/offline'
import { cacheBlob, getBlob } from './blobCache'

const SIGNED_URL_TTL_SECONDS = 3600

export function useCachedImage(storagePath: string): string | null {
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    async function resolve() {
      // 1. Try a signed URL first (the existing behavior).
      try {
        const { data } = await supabase.storage
          .from('song-files')
          .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS)

        if (data?.signedUrl) {
          if (cancelled) return
          setSrc(data.signedUrl)

          // Background-fetch and cache the blob so we survive the next
          // offline reload. Fire and forget.
          if (OFFLINE_ENABLED) {
            void fetchAndCache(data.signedUrl, storagePath)
          }
          return
        }
      } catch (err) {
        console.debug('[offline] signed URL failed, trying cache:', err)
      }

      // 2. Fallback: look in the Cache API.
      if (!OFFLINE_ENABLED) return
      const blob = await getBlob(CACHE_IMAGES, storagePath)
      if (!blob || cancelled) return
      objectUrl = URL.createObjectURL(blob)
      setSrc(objectUrl)
    }

    void resolve()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [storagePath])

  return src
}

async function fetchAndCache(signedUrl: string, storagePath: string): Promise<void> {
  try {
    const res = await fetch(signedUrl)
    if (!res.ok) return
    const blob = await res.blob()
    await cacheBlob(CACHE_IMAGES, storagePath, blob)
  } catch (err) {
    console.debug('[offline] background image cache failed:', err)
  }
}
